<template>
    <slot />
    <ToastContainer ref="toastContainer" />
</template>

<script setup>
import { ref, provide, onMounted, onUnmounted } from 'vue'
import ToastContainer from './ToastContainer.vue'
import { toastBridge } from '@/services/toastBridge'

const toastContainer = ref(null)

// Toast methods to be provided
const show = (message, options = {}) => {
  return toastContainer.value?.addToast(message, options)
}

const success = (message, options = {}) => {
  return show(message, { ...options, type: 'success' })
}

const error = (message, options = {}) => {
  return show(message, { ...options, type: 'danger' })
}

const warning = (message, options = {}) => {
  return show(message, { ...options, type: 'warning' })
}

const info = (message, options = {}) => {
  return show(message, { ...options, type: 'info' })
}

const primary = (message, options = {}) => {
  return show(message, { ...options, type: 'primary' })
}

const persistent = (message, type = 'info') => {
  return show(message, { type, persistent: true })
}

const dismiss = (id) => {
  toastContainer.value?.dismissToast(id)
}

const clearAll = () => {
  toastContainer.value?.clearAll()
}

// Create toast API object
const toastApi = {
  show,
  success,
  error,
  warning,
  info,
  primary,
  persistent,
  dismiss,
  clearAll
}

// Provide the toast API to all child components
provide('toast', toastApi)

// Subscribe to toastBridge for service-layer toasts
let unsubscribe = null

onMounted(() => {
  // Subscribe to bridge events
  unsubscribe = toastBridge.subscribe((event) => {
    const { type, message, options } = event
    
    switch (type) {
      case 'show':
        show(message, options)
        break
      case 'success':
        success(message, options)
        break
      case 'error':
        error(message, options)
        break
      case 'warning':
        warning(message, options)
        break
      case 'info':
        info(message, options)
        break
      case 'primary':
        primary(message, options)
        break
      case 'persistent':
        persistent(message, options?.type || 'info')
        break
      case 'dismiss':
        dismiss(message) // message is the id in this case
        break
      case 'clearAll':
        clearAll()
        break
    }
  })
})

onUnmounted(() => {
  if (unsubscribe) {
    unsubscribe()
  }
})
</script>